import { Component, ReactNode } from "react";
import { EmbeddedTweet, TweetNotFound, TweetSkeleton, useTweet } from "react-tweet";

type TwitterEmbedProps = {
  id: string;
  options?: { conversation?: string; cards?: string };
};

class TweetErrorBoundary extends Component<
  { id: string; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.warn("react-tweet failed to render", this.props.id, error);
  }

  componentDidUpdate(prev: { id: string }) {
    if (prev.id !== this.props.id && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: "12px 16px",
            borderRadius: 12,
            border: "1px solid #E4E7E9",
            color: "#52575D",
            fontSize: 14,
          }}
        >
          This tweet can't be displayed.{" "}
          <a
            href={`https://x.com/i/status/${this.props.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#2A85FF" }}
          >
            Open on X
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}

const apiBase = (import.meta.env.VITE_BASE_URL ?? "").replace(/\/+$/, "");
const buildTweetApiUrl = (id: string) => (apiBase ? `${apiBase}/tweet/${id}` : undefined);

const normalizeTweet = (data: any) => ({
  ...data,
  entities: {
    hashtags: [],
    symbols: [],
    user_mentions: [],
    urls: [],
    ...(data?.entities ?? {}),
  },
});

function TweetView({ id, apiUrl }: { id: string; apiUrl?: string }) {
  const { data, error, isLoading } = useTweet(id, apiUrl);

  if (isLoading) return <TweetSkeleton />;
  if (error || !data) return <TweetNotFound error={error} />;

  return <EmbeddedTweet tweet={normalizeTweet(data)} />;
}

export default function TwitterEmbed({ id }: TwitterEmbedProps) {
  return (
    <div style={{ maxWidth: 550, margin: "0 auto" }} data-theme="light">
      <TweetErrorBoundary id={id}>
        <TweetView id={id} apiUrl={buildTweetApiUrl(id)} />
      </TweetErrorBoundary>
    </div>
  );
}
